import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL;

interface WarningState {
  is_warning_active: boolean;
  events_today: number;
  outbound_today: number;
  pct_today: number;
  streak_active: boolean;
  spike_active: boolean;
}

interface CachedWarning {
  state: WarningState;
  expires_at: number;
}

interface TrustScoreWarningBannerProps {
  locationId: string;
}

interface WarningApiResponse {
  success?: boolean;
  warning?: WarningState;
  error?: string;
}

const CACHE_TTL_MS = 60 * 60 * 1000;
const cacheKey = (locationId: string) => `oak_30023_warning_${locationId}`;

function readCache(locationId: string): WarningState | null {
  try {
    const raw = localStorage.getItem(cacheKey(locationId));
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedWarning;
    if (cached.expires_at > Date.now()) return cached.state;
    return null;
  } catch {
    return null;
  }
}

function writeCache(locationId: string, state: WarningState): void {
  try {
    const cached: CachedWarning = {
      state,
      expires_at: Date.now() + CACHE_TTL_MS,
    };
    localStorage.setItem(cacheKey(locationId), JSON.stringify(cached));
  } catch {
    /* localStorage may be unavailable — fail silently */
  }
}

export function TrustScoreWarningBanner({ locationId }: TrustScoreWarningBannerProps) {
  const [state, setState] = useState<WarningState | null>(null);

  useEffect(() => {
    const cached = readCache(locationId);
    if (cached) {
      setState(cached);
      return;
    }

    let cancelled = false;
    fetch(`${API_URL}/trustscore-warning/${encodeURIComponent(locationId)}`)
      .then((r) => (r.ok ? (r.json() as Promise<WarningApiResponse>) : null))
      .then((payload) => {
        if (cancelled || !payload?.success || !payload.warning) return;
        const next: WarningState = payload.warning;
        setState(next);
        writeCache(locationId, next);
      })
      .catch(() => {
        /* Banner stays hidden on fetch failure; will retry on next page load past TTL */
      });

    return () => {
      cancelled = true;
    };
  }, [locationId]);

  if (!state?.is_warning_active) return null;

  return (
    <div className="w-full bg-amber-50 border-b border-amber-400 text-amber-900 text-sm py-2 px-4">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <p>
          <strong>You've been hitting daily message caps recently.</strong>{' '}
          Carriers like T-Mobile enforce stricter limits than others, and repeatedly hitting these limits can lower your trust score over time.{' '}
          Try scrubbing your lists more aggressively before sending and easing off batches that are heavy on stricter carriers for the next few days.
        </p>
      </div>
    </div>
  );
}
